import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Menu, X, Plane } from 'lucide-react'
import { clsx } from 'clsx'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { publicUrl } from '../utils/publicUrl'

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'About', href: '/about' },
  { name: 'Meet the Team', href: '/meet-the-team' },
  { name: 'Holiday Packages', href: '/packages' },
  { name: 'Discovery Specials', href: '/discovery-specials' },
  { name: 'Blog', href: '/blog' },
  { name: 'Contact us', href: '/contact' },
]

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const location = useLocation()
  const { franchiseData, loading } = useFranchiseData()

  // Show loading state or fallback
  if (loading || !franchiseData) {
    return (
      <header className="absolute top-0 left-0 right-0 z-50 bg-transparent">
        <nav className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
          <div className="flex lg:flex-1">
            <div className="animate-pulse flex items-center space-x-2">
              <div className="h-8 w-8 bg-gray-300 rounded"></div>
              <div className="h-6 w-32 bg-gray-300 rounded"></div>
            </div>
          </div>
        </nav>
      </header>
    )
  }

  // Check if we're on the home page (which has dark hero background)
  const isHomePage = location.pathname === '/'
  
  return (
    <header className={clsx(
      "top-0 left-0 right-0 z-50",
      isHomePage ? "absolute" : "relative bg-indojet-light shadow-sm"
    )}>
      <nav
        className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8"
        aria-label="Global"
      >
        <div className="flex lg:flex-1">
          <Link to="/" className="-m-1.5 p-1.5 flex items-center" style={{ overflow: 'visible' }}>
            <img
              src={publicUrl('indojet-travel-logo-6.png')}
              alt="IndoJet Travel Logo"
              className="w-auto h-14 sm:h-20 md:h-24 lg:h-[120px]"
            />
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className={clsx(
              "-m-2.5 inline-flex items-center justify-center rounded-md p-2.5",
              isHomePage ? "text-white" : "text-gray-700"
            )}
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        
        {/* Center Navigation with Pills */}
        <div className={clsx(
          "hidden lg:flex items-center backdrop-blur-md rounded-full px-2 py-2",
          isHomePage ? "bg-white/10" : "bg-gray-100/80"
        )}>
          {navigation.map((item) => {
            const isOpen = openDropdown === item.name
            const isActive = location.pathname === item.href

            return (
              <div
                key={item.name}
                className="relative"
              >
                <div className="inline-flex items-center">
                  <Link
                    to={item.href}
                    className={clsx(
                      'px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 inline-flex items-center gap-1',
                      isActive
                        ? (isHomePage ? 'bg-white text-gray-900 shadow-lg' : 'bg-[#182c78] text-white shadow-lg')
                        : (isHomePage ? 'text-white/90 hover:text-white hover:bg-white/10' : 'text-gray-700 hover:text-gray-900 hover:bg-gray-200/50')
                    )}
                  >
                    {item.name}
                  </Link>
                  {item.children && (
                    <button
                      type="button"
                      aria-label={`Toggle ${item.name} menu`}
                      className={clsx(
                        'px-1 py-2 text-sm font-medium rounded-full transition-all duration-300',
                        isOpen
                          ? (isHomePage ? 'text-white' : 'text-[#182c78]')
                          : (isHomePage ? 'text-white/90 hover:text-white' : 'text-gray-700 hover:text-gray-900')
                      )}
                      onClick={(e) => {
                        e.preventDefault()
                        setOpenDropdown(isOpen ? null : item.name)
                      }}
                    >
                      <svg
                        className={clsx('w-4 h-4 transition-transform', isOpen && 'rotate-180')}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  )}
                </div>
                {item.children && isOpen && (
                  <div className="absolute left-0 mt-2">
                    <div className="rounded-2xl bg-white shadow-lg ring-1 ring-black/5 py-2 w-48">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          to={child.href}
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                          onClick={() => setOpenDropdown(null)}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Book Now Button */}
        <div className="hidden lg:flex lg:flex-1 lg:justify-end">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-[#182c78] text-white px-6 py-2 rounded-full font-semibold hover:bg-[#182c78]/90 transition-all duration-300 shadow-lg"
          >
            <span>Book Now</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </Link>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden" role="dialog" aria-modal="true">
          <div className="fixed inset-0 z-50"></div>
          <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-indojet-light px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10">
            <div className="flex items-center justify-between">
              <Link to="/" className="-m-1.5 p-1.5 flex items-center">
                  <img
                    src={publicUrl('indojet-travel-logo-6.png')}
                    alt="IndoJet Travel Logo"
                    className="w-auto"
                    style={{ height: '50px' }}
                  />
              </Link>
              <button
                type="button"
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                <span className="sr-only">Close menu</span>
                <X className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="-my-6 divide-y divide-gray-500/10">
                <div className="space-y-2 py-6">
                  {navigation.map((item) => (
                    <div key={item.name}>
                      <Link
                        to={item.href}
                        className={clsx(
                          '-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 hover:bg-indojet-secondary hover:text-white transition',
                          location.pathname === item.href
                            ? 'text-white bg-indojet-secondary'
                            : 'text-gray-900'
                        )}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                      {item.children && (
                        <div className="ml-4 mt-1 space-y-1">
                          {item.children.map((child) => (
                            <Link
                              key={child.name}
                              to={child.href}
                              className="-mx-3 block rounded-lg px-3 py-2 text-sm font-medium leading-6 text-gray-700 hover:bg-gray-100"
                              onClick={() => setMobileMenuOpen(false)}
                            >
                              {child.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
